import React from "react";

import type { BusbudError } from "@busbud/busbud-errors";

import { useWhitelabelContext } from "@app/context/whitelabel-context";
import { useAppContext } from "@app/helpers/hooks";
import type { Features } from "@app/types/experiments";
import type { NapiUser } from "@app/types/napi-types";
import type { SigningMethod } from "@app/types/tracking/account";

import { AppleConnectButton } from "../apple";
import { FBConnectButton } from "../facebook";
import { GoogleConnectButton } from "../google";
import { SDK_ERRORS_TO_IGNORE } from "../utils/constants";

interface Props {
  setInProgress: (isInProgress: boolean) => void;
  onSuccess: (user: NapiUser, sign_in_method?: SigningMethod) => void;
  onError: (err: BusbudError) => void;
  is_sign_in?: boolean;
  email_opt_in?: boolean;
}

const getProviders = (features: Partial<Features>) => {
  const base_providers = [{ id: "Google", Component: GoogleConnectButton }];

  if (!features.HIDE_FACEBOOK_SSO) {
    base_providers.unshift({
      id: "Facebook",
      Component: FBConnectButton
    });
  }

  const all_providers = base_providers.concat({
    id: "Apple",
    Component: AppleConnectButton
  });

  return {
    neredennereye: base_providers,
    default: all_providers
  };
};

export const SocialConnectButtons: React.FC<Props> = ({
  onSuccess,
  onError,
  setInProgress,
  is_sign_in = true,
  email_opt_in = false
}) => {
  const { whitelabel } = useWhitelabelContext();
  const { features } = useAppContext();
  const whitelabel_name = whitelabel?.name;
  const providers = getProviders(features);

  const _providers =
    providers[(whitelabel_name as keyof typeof providers) || "default"] ||
    providers.default;

  const handleError = (err: BusbudError) => {
    // Users actions like closing popup should not be considered as an error
    if (!SDK_ERRORS_TO_IGNORE.includes(err.error)) {
      onError(err);
    }
  };

  if (!features.AUTHENTICATION_SOCIAL_LOGINS) {
    return null;
  }

  return (
    <>
      {_providers.map(({ Component, id }) => (
        <Component
          key={id}
          setInProgress={setInProgress}
          onSuccess={onSuccess}
          onError={handleError}
          is_sign_in={is_sign_in}
          is_email_opt_in={email_opt_in}
        />
      ))}
    </>
  );
};
